import { QRCodeDetailResponse, QRCodeResponse, RoutingRulesConfig } from '../types/qr.types';
import { AnalyticsSummary, QRCodeData } from '../pages/Dashboard/QRCodeDetailPage/types';

// Short code used to route to the hardcoded demo QR code detail page
// (frontend/src/pages/Dashboard/QRCodeDetailPage/SampleQRCodeDetailPage.tsx)
// instead of hitting the real backend. Never a value a real QR code can have
// (backend short codes are generated, not user-chosen).
export const SAMPLE_QR_SHORT_CODE = 'example-product-launch';

const SAMPLE_CREATED_AT = new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString();

function daysAgoIso(daysAgo: number): string {
  return new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
}

// --- Deterministic pseudo-random data generation -------------------------
// A fixed seed keeps the numbers stable across renders/sessions (this is
// still "hardcoded" in the sense the design doc means — no two page loads
// show different data) while still looking like organic traffic rather
// than a hand-typed, suspiciously round series. Breakdown categories are
// split by weightedSplit so they always sum exactly to total_scans,
// mirroring how a real "every scan has exactly one device/browser/country"
// breakdown behaves.

function mulberry32(seed: number) {
  let a = seed;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = mulberry32(20260811);

function weightedSplit(total: number, weights: number[], jitter: number): number[] {
  const weightSum = weights.reduce((sum, w) => sum + w, 0);
  const raw = weights.map((w) => (total * w) / weightSum * (1 + (rng() - 0.5) * jitter));
  const rawSum = raw.reduce((sum, v) => sum + v, 0);
  const scaled = raw.map((v) => (v / rawSum) * total);

  // Largest-remainder rounding so the integer parts sum exactly to `total`.
  const floors = scaled.map(Math.floor);
  let remainder = total - floors.reduce((sum, v) => sum + v, 0);
  const remainders = scaled.map((v, i) => ({ i, frac: v - floors[i] })).sort((a, b) => b.frac - a.frac);
  const result = [...floors];
  for (let k = 0; k < remainder; k++) {
    result[remainders[k].i] += 1;
  }
  return result;
}

// 45 days of daily scans: a mild upward trend, a weekend dip, and noise —
// not a flat or hand-typed-looking series.
const DAY_COUNT = 45;
const dailyCounts: number[] = [];
for (let i = 0; i < DAY_COUNT; i++) {
  const dayOfWeek = new Date(Date.now() - (DAY_COUNT - 1 - i) * 24 * 60 * 60 * 1000).getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const trend = 60 + i * 2.4; // gentle ramp as the campaign gains traction
  const weekendDip = isWeekend ? 0.65 : 1;
  const noise = 0.75 + rng() * 0.5;
  dailyCounts.push(Math.max(5, Math.round(trend * weekendDip * noise)));
}

const TOTAL_SCANS = dailyCounts.reduce((sum, c) => sum + c, 0);
const UNIQUE_RATE = 0.6 + rng() * 0.15; // 60-75% of scans are from a unique visitor
const UNIQUE_USERS = Math.round(TOTAL_SCANS * UNIQUE_RATE);

const [mobileScans, desktopScans, tabletScans] = weightedSplit(TOTAL_SCANS, [0.62, 0.29, 0.09], 0.2);
const [chromeScans, safariScans, firefoxScans, edgeScans] = weightedSplit(TOTAL_SCANS, [0.5, 0.28, 0.12, 0.1], 0.25);
const [usScans, caScans, ukScans, deScans, frScans, auScans] = weightedSplit(
  TOTAL_SCANS,
  [0.38, 0.19, 0.15, 0.11, 0.09, 0.08],
  0.3
);
const [launchUrlScans, supportUrlScans, euUrlScans] = weightedSplit(TOTAL_SCANS, [0.78, 0.14, 0.08], 0.2);

// Shown as a row in "Recent QR Codes" (frontend/src/components/dashboard/QRCodeTable.tsx),
// which expects the same shape the real list endpoint returns. Inactive so
// it reads clearly as a demo, not a live campaign.
export const sampleQRCodeListEntry: QRCodeResponse = {
  id: -1,
  user_id: -1,
  short_code: SAMPLE_QR_SHORT_CODE,
  title: 'Product Launch Flyer',
  description: 'Demo QR code for a product launch campaign printed on flyers and posters.',
  fallback_url: 'https://example.com/launch',
  is_active: false,
  password_protected: false,
  has_rules: true,
  rules_count: 2,
  created_at: SAMPLE_CREATED_AT,
  updated_at: SAMPLE_CREATED_AT,
  tags: [],
};

// Fed directly to the same components the real detail page uses
// (QRCodeHeader, StatsGrid, AnalyticsCharts, RoutingRulesSection) — all pure
// render-from-props, so no API calls happen for this fake entry.
export const sampleQRCodeData: QRCodeData = {
  id: -1,
  title: sampleQRCodeListEntry.title,
  description: sampleQRCodeListEntry.description,
  fallback_url: sampleQRCodeListEntry.fallback_url,
  short_code: SAMPLE_QR_SHORT_CODE,
  created_at: SAMPLE_CREATED_AT,
  is_active: false,
  has_rules: true,
  rules_count: 2,
  password_protected: false,
  routing_rules: {
    rules: [
      {
        id: 'sample-rule-1',
        name: 'Business hours redirect',
        enabled: true,
        conditions: {
          operator: 'AND',
          items: [
            {
              type: 'time',
              start_time: '09:00',
              end_time: '17:00',
              days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            },
          ],
        },
        action: { type: 'redirect', url: 'https://example.com/support' },
      },
      {
        id: 'sample-rule-2',
        name: 'EU visitors',
        enabled: true,
        conditions: {
          operator: 'AND',
          items: [{ type: 'geographic', countries: ['DE', 'FR', 'ES'] }],
        },
        action: { type: 'redirect', url: 'https://example.com/eu' },
      },
    ],
  },
};

// The qr.types.RoutingRulesConfig shape (version + priority-ordered rules)
// used by the real create/update API and the edit form — distinct from the
// simplified RoutingRules type QRCodeDetailPage's own components render.
const sampleEditableRoutingRules: RoutingRulesConfig = {
  version: 1,
  rules: [
    {
      id: 'sample-rule-1',
      name: 'Business hours redirect',
      priority: 0,
      enabled: true,
      conditions: {
        operator: 'AND',
        items: [
          {
            type: 'time',
            start_time: '09:00',
            end_time: '17:00',
            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            timezone: 'UTC',
          },
        ],
      },
      action: { type: 'redirect', url: 'https://example.com/support' },
    },
    {
      id: 'sample-rule-2',
      name: 'EU visitors',
      priority: 1,
      enabled: true,
      conditions: {
        operator: 'AND',
        items: [{ type: 'geographic', countries: ['DE', 'FR', 'ES'], regions: [], cities: [] }],
      },
      action: { type: 'redirect', url: 'https://example.com/eu' },
    },
  ],
};

// Fed to QRCodeEditForm in readOnly mode (frontend/src/pages/Dashboard/QRCodeEditPage.tsx's
// sample counterpart) — every field, including routing rules, stays interactive, but the
// form's single save mutation is short-circuited since there's no real row to persist to.
export const sampleQRCodeDetailResponse: QRCodeDetailResponse = {
  ...sampleQRCodeListEntry,
  routing_rules: sampleEditableRoutingRules,
};

export const sampleAnalytics: AnalyticsSummary = {
  qr_code_id: -1,
  total_scans: TOTAL_SCANS,
  unique_users: UNIQUE_USERS,
  device_breakdown: [
    { device: 'Mobile', count: mobileScans },
    { device: 'Desktop', count: desktopScans },
    { device: 'Tablet', count: tabletScans },
  ],
  country_breakdown: [
    { country: 'United States', count: usScans },
    { country: 'Canada', count: caScans },
    { country: 'United Kingdom', count: ukScans },
    { country: 'Germany', count: deScans },
    { country: 'France', count: frScans },
    { country: 'Australia', count: auScans },
  ],
  browser_breakdown: [
    { browser: 'Chrome', count: chromeScans },
    { browser: 'Safari', count: safariScans },
    { browser: 'Firefox', count: firefoxScans },
    { browser: 'Edge', count: edgeScans },
  ],
  url_breakdown: [
    { url: 'https://example.com/launch', count: launchUrlScans },
    { url: 'https://example.com/support', count: supportUrlScans },
    { url: 'https://example.com/eu', count: euUrlScans },
  ],
  hourly_breakdown: Array.from({ length: 24 }, (_, hour) => {
    // Business-hours peak around 2pm with a long tail, plus noise.
    const base = 20 + 140 * Math.exp(-Math.pow(hour - 14, 2) / 28);
    return { hour, count: Math.max(2, Math.round(base * (0.8 + rng() * 0.4))) };
  }),
  daily_scans: dailyCounts.map((count, i) => ({ date: daysAgoIso(DAY_COUNT - 1 - i), count })),
  last_scan_at: daysAgoIso(0),
};
